<template>
  <div class="tracking-page">
    <tracking-page-header
      title="Busy times"
      subtitle="See when patients visit and book. Use this to decide when more doctors or slots are needed."
      :from-date="fromDate"
      :to-date="toDate"
      :loading="loading"
      :chat-filters="chatFilters"
      :page-summary="pageSummary"
      @apply="fromDate = $event.fromDate; toDate = $event.toDate; loadData()"
      @refresh="loadData"
    >
      <template #actions>
        <select class="form-control" style="width:auto" v-model="granularity" @change="loadData">
          <option value="hour">By hour</option>
          <option value="day">By day</option>
          <option value="week">By week</option>
        </select>
      </template>
    </tracking-page-header>

    <div class="trk-kpi-grid">
      <div class="trk-kpi"><div class="trk-kpi-label">People who started</div><div class="trk-kpi-value">{{ formatNumber(traffic.totalStarted) }}</div></div>
      <div class="trk-kpi"><div class="trk-kpi-label">People who booked</div><div class="trk-kpi-value">{{ formatNumber(traffic.totalBookings) }}</div></div>
      <div class="trk-kpi"><div class="trk-kpi-label">Busiest moment (est.)</div><div class="trk-kpi-value">{{ formatNumber(traffic.peakConcurrentEstimate) }}</div><div class="trk-kpi-help">Approx. people active at once</div></div>
    </div>

    <div class="trk-panel">
      <div class="trk-panel-head">
        <div>
          <h2>Activity chart</h2>
          <p>Timezone: {{ traffic.timezone || 'Europe/London' }}</p>
        </div>
      </div>
      <div class="trk-panel-body">
        <div class="trk-bar-chart" v-if="chartBars.length">
          <div class="trk-bar-col" v-for="(bar, idx) in chartBars" :key="idx">
            <div class="trk-bar" :style="{ height: bar.height + 'px' }" :title="bar.tooltip"></div>
            <div class="trk-bar-label">{{ bar.label }}</div>
          </div>
        </div>
        <div class="trk-empty" v-else>
          <strong>No traffic in this range</strong>
          Try a wider date range.
        </div>
      </div>
    </div>

    <div class="trk-panel">
      <div class="trk-panel-head"><div><h2>Detailed breakdown</h2><p>Exact numbers by time bucket</p></div></div>
      <div class="trk-panel-body p-0">
        <table class="table trk-table mb-0" v-trk-resizable="'traffic-breakdown'">
          <thead>
            <tr><th>Time</th><th>Hour</th><th>Started</th><th>Booked</th><th>Left</th></tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in (traffic.buckets || [])" :key="idx">
              <td>{{ prettyBucket(row.bucketStart) }}</td>
              <td>{{ row.hourOfDay >= 0 ? row.hourOfDay + ':00' : '-' }}</td>
              <td>{{ formatNumber(row.started) }}</td>
              <td>{{ formatNumber(row.bookings) }}</td>
              <td>{{ formatNumber(row.abandoned) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import TrackingPageHeader from './TrackingPageHeader.vue';
import { defaultDateRange, formatNumber, rangeParams, formatUkDateTime } from './trackingShared';

export default {
  name: 'TrackingTraffic',
  components: { TrackingPageHeader },
  data() {
    const range = defaultDateRange(7);
    return {
      fromDate: range.fromDate,
      toDate: range.toDate,
      granularity: 'hour',
      traffic: {},
      loading: false
    };
  },
  computed: {
    chatFilters() {
      return {
        ...rangeParams(this.fromDate, this.toDate),
        granularity: this.granularity
      };
    },
    pageSummary() {
      return `${this.formatNumber(this.traffic.totalStarted)} started, ${this.formatNumber(this.traffic.totalBookings)} booked (${this.granularity})`;
    },
    chartBars() {
      const buckets = this.traffic.buckets || [];
      const max = Math.max(1, ...buckets.map((b) => Number(b.started || 0)));
      return buckets.slice(-24).map((b) => ({
        height: Math.max(6, Math.round((Number(b.started || 0) * 150) / max)),
        label: b.hourOfDay >= 0 ? `${b.hourOfDay}h` : this.prettyBucket(b.bucketStart).slice(5, 10),
        tooltip: `${this.prettyBucket(b.bucketStart)} — ${b.started} started, ${b.bookings} booked`
      }));
    }
  },
  created() { this.loadData(); },
  methods: {
    formatNumber,
    prettyBucket(value) {
      if (!value) return '-';
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      return formatUkDateTime(d, { withSeconds: false });
    },
    loadData() {
      this.loading = true;
      this.$onboardingTrackingService.getTraffic({
        ...rangeParams(this.fromDate, this.toDate),
        granularity: this.granularity,
        timezone: 'Europe/London'
      }).then((res) => {
        this.traffic = (res && res.data) || {};
      }).finally(() => { this.loading = false; });
    }
  }
};
</script>
